import React, { useMemo, useState } from 'react'
import {
  ArrowUpDown,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  Package,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Truck,
} from 'lucide-react'
import GlobalHeader from '../components/GlobalHeader'
import {
  useRetailScorecard,
  useRetailSKUPerformance,
  useRetailSellThroughTrends,
  RetailerScorecard,
  SKUPerformance,
} from '../lib/data-service'
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  LineChart,
} from 'recharts'

const RETAILER_META: Record<string, { display: string; color: string; bg: string; border: string; text: string }> = {
  ulta: { display: 'Ulta Beauty', color: '#f97316', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  macys: { display: "Macy's", color: '#ef4444', bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
  jcp: { display: 'JCPenney', color: '#3b82f6', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
}

const WEEK_OPTIONS = [
  { label: '4 Weeks', value: 4 },
  { label: '8 Weeks', value: 8 },
  { label: '13 Weeks', value: 13 },
  { label: '26 Weeks', value: 26 },
]

const TREND_RETAILER_OPTIONS = [
  { label: 'Macys', value: 'macys' },
  { label: 'Ulta', value: 'ulta' },
  { label: 'JCPenney', value: 'jcp' },
]

const TREND_LOOKBACK_WEEKS = 12

function meta(slug: string) {
  return RETAILER_META[slug] || {
    display: slug,
    color: '#64748b',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/30',
    text: 'text-slate-300',
  }
}

function fmt$(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}

function fmtN(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
  return v.toLocaleString()
}

function fmtPct(v: number, digits = 1) {
  return `${v.toFixed(digits)}%`
}

function getWeeksOfSupplyValue(scorecard: RetailerScorecard, selectedWeeks: number) {
  if (Number.isFinite(scorecard.weeks_of_supply) && scorecard.weeks_of_supply > 0) {
    return scorecard.weeks_of_supply
  }
  const lookback = Math.max(selectedWeeks, 1)
  const avgWeeklySellThrough = scorecard.sellthru_units / lookback
  if (avgWeeklySellThrough <= 0) return 0
  const recentSellInUnits = lookback >= 4
    ? (scorecard.sellin_units / lookback) * 4
    : scorecard.sellin_units
  return recentSellInUnits / avgWeeklySellThrough
}

type SkuRiskFlag = 'overstock' | 'stockout' | 'ok'

function getSkuRiskFlag(sku: SKUPerformance): SkuRiskFlag {
  const sellThroughRate = Number.isFinite(sku.sellthru_rate_pct) ? sku.sellthru_rate_pct : 0
  const poVelocityDeclining = Boolean((sku as SKUPerformance & { po_velocity_declining?: boolean }).po_velocity_declining)
  if (sellThroughRate < 40) return 'overstock'
  if (sellThroughRate > 90 && poVelocityDeclining) return 'stockout'
  return 'ok'
}

function DeltaChip({ value, suffix = '%' }: { value: number; suffix?: string }) {
  if (!Number.isFinite(value) || value === 0) return <span className="text-xs text-muted-foreground">—</span>
  const positive = value > 0
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}{value.toFixed(1)}{suffix}
    </span>
  )
}

function RiskFlagBadge({ risk }: { risk: SkuRiskFlag }) {
  const styles: Record<SkuRiskFlag, string> = {
    overstock: 'bg-red-500/15 text-red-400 border-red-500/30',
    stockout: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    ok: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  }
  const labels: Record<SkuRiskFlag, string> = {
    overstock: 'Overstock',
    stockout: 'Stockout',
    ok: 'OK',
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${styles[risk]}`}>
      {labels[risk]}
    </span>
  )
}

function ScorecardCard({
  data,
  isSelected,
  weeksOfSupply,
  onClick,
}: {
  data: RetailerScorecard
  isSelected: boolean
  weeksOfSupply: number
  onClick: () => void
}) {
  const m = meta(data.retailer)

  return (
    <button
      onClick={onClick}
      aria-pressed={isSelected}
      className={`w-full text-left rounded-xl border p-5 transition-all ${
        isSelected
          ? `${m.bg} ${m.border}`
          : 'bg-card border-border hover:border-muted-foreground/30'
      }`}
      style={isSelected ? { boxShadow: `inset 0 0 0 1px ${m.color}` } : undefined}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${isSelected ? m.text : 'text-foreground'}`}>
            {m.display}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">Click to drill into SKU performance</p>
        </div>
        <span className="text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground">
          {isSelected ? 'Open' : 'Drill-Down'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sell-In (POs)</p>
          <p className="text-xl font-bold">{fmt$(data.sellin_revenue)}</p>
          <p className="text-xs text-muted-foreground">{fmtN(data.sellin_units)} units</p>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sell-Through</p>
          <p className="text-xl font-bold">{fmt$(data.sellthru_revenue)}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{fmtN(data.sellthru_units)} units</span>
            <DeltaChip value={data.sellthru_yoy_pct} />
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">ST Rate</p>
          <p className={`text-lg font-bold ${
            data.sellthru_rate_pct >= 90 ? 'text-emerald-400'
              : data.sellthru_rate_pct >= 40 ? 'text-foreground'
                : 'text-red-400'
          }`}>
            {data.sellthru_rate_pct > 0 ? fmtPct(data.sellthru_rate_pct, 0) : 'N/A'}
          </p>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Margin</p>
          <p className="text-lg font-bold">{fmtPct(data.sellin_margin_pct, 1)}</p>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span>{data.po_count} POs</span>
          <span>{data.sku_count} SKUs</span>
        </div>
        <span className={`font-semibold ${
          weeksOfSupply > 10 ? 'text-amber-400'
            : weeksOfSupply > 0 ? 'text-emerald-400'
              : 'text-muted-foreground'
        }`}>
          Weeks of Supply: {weeksOfSupply > 0 ? weeksOfSupply.toFixed(1) : 'N/A'}
        </span>
      </div>
    </button>
  )
}

type SortField = 'sku_name' | 'sellin_units' | 'sellthru_units' | 'sellthru_rate_pct' | 'sellin_margin_pct' | 'risk'

type EnrichedSku = SKUPerformance & { riskFlag: SkuRiskFlag }

function SKUTable({ retailer, weeks }: { retailer: string; weeks: number }) {
  const { data, isLoading } = useRetailSKUPerformance(retailer, weeks)
  const [sortField, setSortField] = useState<SortField>('risk')
  const [sortAsc, setSortAsc] = useState(false)

  const enrichedSkus = useMemo<EnrichedSku[]>(() => {
    if (!data?.skus) return []
    return data.skus.map((sku) => ({
      ...sku,
      riskFlag: getSkuRiskFlag(sku),
    }))
  }, [data?.skus])

  const sortedSkus = useMemo(() => {
    const riskWeight: Record<SkuRiskFlag, number> = { stockout: 3, overstock: 2, ok: 1 }
    const getSortValue = (sku: EnrichedSku): string | number => {
      if (sortField === 'sku_name') {
        return (sku.product_name || sku.base_sku || sku.sku || '').toLowerCase()
      }
      if (sortField === 'risk') return riskWeight[sku.riskFlag]
      return sku[sortField] ?? 0
    }

    return [...enrichedSkus].sort((a, b) => {
      const av = getSortValue(a)
      const bv = getSortValue(b)
      let compare = 0
      if (typeof av === 'string' || typeof bv === 'string') {
        compare = String(av).localeCompare(String(bv))
      } else {
        compare = av - bv
      }
      return sortAsc ? compare : -compare
    })
  }, [enrichedSkus, sortField, sortAsc])

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev)
      return
    }
    setSortField(field)
    setSortAsc(false)
  }

  const SortControl = ({ field }: { field: SortField }) => (
    <button
      onClick={() => toggleSort(field)}
      className="ml-1 text-muted-foreground hover:text-foreground"
      aria-label={`Sort by ${field}`}
    >
      {sortField === field
        ? (sortAsc ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />)
        : <ArrowUpDown className="w-3 h-3 inline opacity-40" />
      }
    </button>
  )

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading SKU drill-down...</div>
  }

  if (!sortedSkus.length) {
    return <div className="text-sm text-muted-foreground py-8 text-center">No SKU data available for this retailer.</div>
  }

  const m = meta(retailer)

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Package className="w-4 h-4" />
          SKU Drill-Down — {m.display}
        </h3>
        <span className="text-xs text-muted-foreground">{sortedSkus.length} SKUs</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 text-xs text-muted-foreground uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium whitespace-nowrap">SKU Name <SortControl field="sku_name" /></th>
              <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Sell-In Units <SortControl field="sellin_units" /></th>
              <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Sell-Through Units <SortControl field="sellthru_units" /></th>
              <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Sell-Through Rate <SortControl field="sellthru_rate_pct" /></th>
              <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Margin % <SortControl field="sellin_margin_pct" /></th>
              <th className="text-center px-4 py-3 font-medium whitespace-nowrap">Risk Flag <SortControl field="risk" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {sortedSkus.map((sku) => (
              <tr key={`${sku.sku}-${sku.base_sku}`} className="hover:bg-muted/20 transition-colors">
                <td className="px-4 py-3 min-w-[260px]">
                  <p className="font-medium text-foreground truncate">{sku.product_name || sku.base_sku}</p>
                  <p className="text-xs text-muted-foreground">{sku.base_sku}</p>
                </td>
                <td className="text-right px-4 py-3 font-mono">{fmtN(sku.sellin_units)}</td>
                <td className="text-right px-4 py-3 font-mono">{fmtN(sku.sellthru_units)}</td>
                <td className="text-right px-4 py-3">
                  <span className={`font-mono ${
                    sku.sellthru_rate_pct < 40 ? 'text-red-400'
                      : sku.sellthru_rate_pct > 90 ? 'text-amber-400'
                        : 'text-emerald-400'
                  }`}>
                    {fmtPct(sku.sellthru_rate_pct, 1)}
                  </span>
                </td>
                <td className="text-right px-4 py-3 font-mono">{fmtPct(sku.sellin_margin_pct, 1)}</td>
                <td className="text-center px-4 py-3">
                  <RiskFlagBadge risk={sku.riskFlag} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TrendChart({ retailer }: { retailer: string }) {
  const { data, isLoading } = useRetailSellThroughTrends(TREND_LOOKBACK_WEEKS, retailer)

  const chartData = useMemo(() => {
    if (!data?.trends) return []
    return [...data.trends]
      .map((week: any) => {
        const retailers = Array.isArray(week.retailers) ? week.retailers : []
        const retailerRow = retailers.find((row: any) => row.retailer === retailer) || retailers[0] || {}
        return {
          fiscal_week: String(week.fiscal_week),
          sellin_units: Number(retailerRow.sellin_units || 0),
          sellthru_units: Number(retailerRow.sellthru_units || 0),
          sellin_revenue: Number(retailerRow.sellin_revenue || 0),
          sellthru_revenue: Number(retailerRow.sellthru_revenue || 0),
        }
      })
      .sort((a, b) => a.fiscal_week.localeCompare(b.fiscal_week))
      .slice(-TREND_LOOKBACK_WEEKS)
  }, [data?.trends, retailer])

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-10 text-center">Loading trend chart...</div>
  }

  if (!chartData.length) {
    return <div className="text-sm text-muted-foreground py-10 text-center">No trend data available for this retailer.</div>
  }

  return (
    <ResponsiveContainer width="100%" height={420}>
      <LineChart data={chartData} margin={{ top: 8, right: 30, left: 8, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="fiscal_week"
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          tickFormatter={(v) => `W${String(v).slice(-2)}`}
        />
        <YAxis
          yAxisId="units"
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          tickFormatter={(v: number) => fmtN(v)}
          label={{
            value: 'Units',
            angle: -90,
            position: 'insideLeft',
            style: { fill: 'hsl(var(--muted-foreground))', fontSize: 11 },
          }}
        />
        <YAxis
          yAxisId="revenue"
          orientation="right"
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
          tickFormatter={(v: number) => fmt$(v)}
          label={{
            value: 'Revenue ($)',
            angle: 90,
            position: 'insideRight',
            style: { fill: 'hsl(var(--muted-foreground))', fontSize: 11 },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '10px',
            fontSize: '12px',
          }}
          labelFormatter={(label) => `Fiscal Week ${label}`}
          formatter={(value: number, name: string) => {
            if (name.includes('Revenue')) return [fmt$(value), name]
            return [fmtN(value), name]
          }}
        />
        <Legend wrapperStyle={{ fontSize: '12px' }} />

        <Line
          yAxisId="units"
          dataKey="sellin_units"
          name="Sell-In Units"
          stroke="#2563eb"
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          yAxisId="units"
          dataKey="sellthru_units"
          name="Sell-Through Units"
          stroke="#16a34a"
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          yAxisId="revenue"
          dataKey="sellin_revenue"
          name="Sell-In Revenue"
          stroke="#2563eb"
          strokeWidth={2}
          strokeDasharray="6 4"
          opacity={0.6}
          dot={false}
        />
        <Line
          yAxisId="revenue"
          dataKey="sellthru_revenue"
          name="Sell-Through Revenue"
          stroke="#16a34a"
          strokeWidth={2}
          strokeDasharray="6 4"
          opacity={0.6}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function KPIStrip({ retailers }: { retailers: RetailerScorecard[] }) {
  const totals = useMemo(() => {
    return retailers.reduce((acc, r) => ({
      sellin_revenue: acc.sellin_revenue + r.sellin_revenue,
      sellthru_revenue: acc.sellthru_revenue + r.sellthru_revenue,
      sellin_units: acc.sellin_units + r.sellin_units,
      sellthru_units: acc.sellthru_units + r.sellthru_units,
      po_count: acc.po_count + r.po_count,
    }), { sellin_revenue: 0, sellthru_revenue: 0, sellin_units: 0, sellthru_units: 0, po_count: 0 })
  }, [retailers])

  const overallRate = totals.sellin_units > 0 ? (totals.sellthru_units / totals.sellin_units) * 100 : 0

  const kpis = [
    { label: 'Sell-In Revenue', value: fmt$(totals.sellin_revenue), icon: Truck, color: 'text-blue-400' },
    { label: 'Sell-Through Revenue', value: fmt$(totals.sellthru_revenue), icon: ShoppingCart, color: 'text-emerald-400' },
    { label: 'Sell-Through Rate', value: overallRate > 0 ? fmtPct(overallRate, 1) : 'N/A', icon: TrendingUp, color: overallRate >= 90 ? 'text-emerald-400' : 'text-amber-400' },
    { label: 'Total POs', value: totals.po_count.toString(), icon: Clock, color: 'text-orange-400' },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {kpis.map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon className={`w-4 h-4 ${color}`} />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
          </div>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      ))}
    </div>
  )
}

export default function RetailPerformance() {
  const [weeks, setWeeks] = useState(13)
  const [selectedRetailer, setSelectedRetailer] = useState<string | null>(null)
  const [trendRetailer, setTrendRetailer] = useState('ulta')
  const { data: scorecardData, isLoading } = useRetailScorecard(weeks)

  const retailers = scorecardData?.retailers || []

  return (
    <div className="space-y-6">
      <GlobalHeader
        title="Retail Performance"
        subtitle="Sell-in vs sell-through analytics across retail partners"
      />

      <div className="flex flex-wrap items-center gap-2">
        {WEEK_OPTIONS.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => setWeeks(value)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              weeks === value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Loading scorecard data...</div>
      ) : (
        <>
          <KPIStrip retailers={retailers} />

          <div>
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Retailer Scorecard
              </h2>
              <span className="text-xs text-muted-foreground">Click a retailer card to open SKU drill-down</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {retailers.map((r) => (
                <ScorecardCard
                  key={r.retailer}
                  data={r}
                  isSelected={selectedRetailer === r.retailer}
                  weeksOfSupply={getWeeksOfSupplyValue(r, weeks)}
                  onClick={() => {
                    const nextRetailer = selectedRetailer === r.retailer ? null : r.retailer
                    setSelectedRetailer(nextRetailer)
                    if (nextRetailer) {
                      setTrendRetailer(nextRetailer)
                    }
                  }}
                />
              ))}
            </div>
          </div>

          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Sell-In vs Sell-Through Trend
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Weekly trend for the last 12 weeks. Left axis = units, right axis = revenue ($).
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                Retailer
                <select
                  value={trendRetailer}
                  onChange={(event) => setTrendRetailer(event.target.value)}
                  className="bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {TREND_RETAILER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <TrendChart retailer={trendRetailer} />
          </section>

          {selectedRetailer ? (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                SKU Drill-Down
              </h2>
              <SKUTable retailer={selectedRetailer} weeks={weeks} />
            </section>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
              Select a retailer scorecard card to view SKU drill-down.
            </div>
          )}
        </>
      )}
    </div>
  )
}
